/**
 * Translation on the device (SEO_BRIEF_10 section 2.1 step 2): Chrome's built-in `Translator`
 * on a computer, where it has the language pair. Nothing is sent anywhere for it.
 *
 *   Translator.availability({sourceLanguage, targetLanguage})
 *       'available'                  ready now
 *       'downloadable' | 'downloading'  ready once its language pack is on the device
 *       'unavailable'                not this pair: the translator asks the server
 *   Translator.create({..., monitor}) the pair's translator; `downloadprogress` events say how
 *                                    far its pack has come (`loaded`, 0 to 1)
 *
 * `create` may need a tap when a pack has to be downloaded, so `prepare` is called from the
 * microphone's tap and from a language picked; a pair asked for later without one is tried
 * all the same, and a refusal sends that pair to the server. Every answer is kept per pair, so
 * a pair is looked up once a visit. Phones have no `Translator`, and every pair is the
 * server's there: `deviceTranslators` is then null.
 */

export type Availability = 'available' | 'downloadable' | 'downloading' | 'unavailable';

export interface TranslatorLike {
  translate(text: string): Promise<string>;
  destroy?(): void;
}

export interface DownloadMonitor {
  addEventListener(type: 'downloadprogress', listener: (event: { loaded: number }) => void): void;
}

export interface TranslatorStatic {
  availability(options: { sourceLanguage: string; targetLanguage: string }): Promise<string>;
  create(options: {
    sourceLanguage: string;
    targetLanguage: string;
    monitor?: (monitor: DownloadMonitor) => void;
  }): Promise<TranslatorLike>;
}

/** How long `availability` is waited for before the pair goes to the server. */
export const AVAILABILITY_WAIT_MS = 3000;
/**
 * How long `create` may go without answering or reporting progress before the pair goes to the
 * server: a pack download that has stalled, or a browser that never answers.
 */
export const STALL_MS = 20_000;

export interface DeviceTranslators {
  /** Start looking up, and if need be downloading, the pair's translator. From a tap. */
  prepare(from: string, to: string): void;
  /**
   * The pair's translator once it is ready (after its pack downloads, when one does), or null
   * when the device has none for it.
   */
  get(from: string, to: string): Promise<TranslatorLike | null>;
  /** Whether the pair is known to be the device's (looked up and not unavailable). */
  likely(from: string, to: string): boolean;
}

export interface DeviceEvents {
  /**
   * A pack is downloading: how far, 0 to 1; null once it is done (or failed, or given up on).
   * Never for a pair already on the device, and nothing after the null.
   */
  onProgress?(loaded: number | null): void;
}

function within<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      },
    );
  });
}

export function createDeviceTranslators(
  api: TranslatorStatic,
  events: DeviceEvents = {},
): DeviceTranslators {
  const pairs = new Map<string, Promise<TranslatorLike | null>>();
  const known = new Map<string, Availability>();
  let downloading = 0;

  function look(from: string, to: string): Promise<TranslatorLike | null> {
    const key = `${from}>${to}`;
    const held = pairs.get(key);
    if (held) return held;
    const pending = (async () => {
      const said = await within(
        api.availability({ sourceLanguage: from, targetLanguage: to }),
        AVAILABILITY_WAIT_MS,
        'unavailable',
      );
      const availability: Availability =
        said === 'available' || said === 'downloadable' || said === 'downloading'
          ? said
          : 'unavailable';
      known.set(key, availability);
      if (availability === 'unavailable') return null;
      const download = availability !== 'available';
      /** The look is over (made, refused or given up on): what its monitor says comes too late. */
      let settled = false;
      if (download) {
        downloading += 1;
        events.onProgress?.(0);
      }
      try {
        const made = await new Promise<TranslatorLike | null>((resolve, reject) => {
          // A download that stops moving (or a create that never answers) is given up on, so
          // the sentences waiting for it go to the server instead of waiting for ever.
          let watchdog: ReturnType<typeof setTimeout> | null = null;
          const rearm = () => {
            if (watchdog !== null) clearTimeout(watchdog);
            watchdog = setTimeout(() => resolve(null), STALL_MS);
          };
          rearm();
          api
            .create({
              sourceLanguage: from,
              targetLanguage: to,
              monitor(m) {
                m.addEventListener('downloadprogress', (e) => {
                  if (settled) return;
                  const loaded = Number(e.loaded);
                  if (!Number.isFinite(loaded)) return;
                  rearm();
                  // Only a pack that is coming down shows its line. A pair already on the device
                  // reports 0 and 1 all the same, and nothing would hide the line after them.
                  if (download) events.onProgress?.(Math.max(0, Math.min(1, loaded)));
                });
              },
            })
            .then(
              (translator) => {
                if (watchdog !== null) clearTimeout(watchdog);
                resolve(translator);
              },
              (err: unknown) => {
                if (watchdog !== null) clearTimeout(watchdog);
                reject(err);
              },
            );
        });
        if (!made) known.set(key, 'unavailable');
        return made;
      } catch {
        // Refused (no tap to download it from, no space, a policy): this pair is the server's.
        known.set(key, 'unavailable');
        return null;
      } finally {
        settled = true;
        if (download) {
          downloading -= 1;
          if (downloading === 0) events.onProgress?.(null);
        }
      }
    })();
    pairs.set(key, pending);
    return pending;
  }

  return {
    prepare(from, to) {
      void look(from, to);
    },
    get: look,
    likely(from, to) {
      const said = known.get(`${from}>${to}`);
      return said !== undefined && said !== 'unavailable';
    },
  };
}

/** The browser's `Translator`, or null where it has none (every phone, Safari, Firefox). */
export function deviceTranslators(
  scope: object = globalThis,
  events: DeviceEvents = {},
): DeviceTranslators | null {
  const api = (scope as { Translator?: unknown }).Translator as TranslatorStatic | undefined;
  if (
    !api ||
    (typeof api !== 'function' && typeof api !== 'object') ||
    typeof api.availability !== 'function' ||
    typeof api.create !== 'function'
  ) {
    return null;
  }
  return createDeviceTranslators(api, events);
}
